import * as React from "react";
import { InferResponseType } from "hono";
import { applySnapshot, getSnapshot, SnapshotIn } from "mobx-state-tree";

import { HonoClient, useHonoClient } from "~/hooks/HonoContext";
import { useSessionContext } from "~/lib/supabase";
import { storageAdapter } from "../mmkvStorageAdapter";
import { Account, GlobalStore, GlobalStoreType } from "./store";

type GlobalStoreContext =
  | { loading: true; store: null; error: null }
  | LoadedGlobalStoreContext
  | { loading: false; store: null; error: Error };

type LoadedGlobalStoreContext = {
  loading: false;
  store: GlobalStoreType;
  error: null;
};

const GlobalStoreContext = React.createContext<
  | (GlobalStoreContext & {
      refreshStore: () => Promise<GlobalStoreType>;
      clearStore: () => Promise<GlobalStoreType>;
    })
  | null
>(null);

export function useGlobalStore<
  T extends boolean = false,
  TOut = (T extends true ? LoadedGlobalStoreContext : GlobalStoreContext) & {
    refreshStore: () => Promise<GlobalStoreType>;
    clearStore: () => Promise<GlobalStoreType>;
  },
>(assumeLoaded?: T): TOut {
  const storeContext = React.useContext(GlobalStoreContext);
  if (!storeContext) {
    throw new Error("useGlobalStore must be used within a GlobalStoreProvider");
  }
  if (assumeLoaded) {
    if (storeContext.loading) {
      throw new Error("assumeLoaded is true but the store is still loading");
    }
    if (storeContext.error) {
      throw new Error("assumeLoaded is true but the store loading errored");
    }
    if (!storeContext.store) {
      throw new Error("assumeLoaded is true but the store is missing");
    }
  }
  return storeContext as TOut;
}

export function GlobalStoreProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { session } = useSessionContext();
  const { hc } = useHonoClient();

  const [storeContext, setStoreContext] = React.useState<GlobalStoreContext>({
    loading: true,
    store: null,
    error: null,
  });

  const refreshStoreCallback = React.useCallback(() => {
    if (storeContext.store && session)
      return refreshStore(storeContext.store, hc).then((store) =>
        saveStore(store, session.user.id),
      );
    throw new Error("Store is not loaded");
  }, [storeContext.store, hc, session]);

  const clearStoreCallback = React.useCallback(() => {
    if (storeContext.store && session) {
      setStoreContext({ loading: true, store: null, error: null });
      return clearStore(storeContext.store, session.user.id);
    }
    throw new Error("Store is not loaded");
  }, [storeContext.store, session]);

  React.useEffect(() => {
    if (!storeContext.store) {
      async function initStore() {
        let store: GlobalStoreType | null = null;
        let error: Error | null = null;
        if (session) {
          // try to load the store from local storage
          try {
            const data = storageAdapter.getItem(
              `@globalStore:${session.user.id}`,
            );
            if (data) {
              const localData = JSON.parse(data);
              store = GlobalStore.create(localData);
              // schedule a refresh in the background
              refreshStore(store, hc).then((store) =>
                saveStore(store, session.user.id),
              );
            }
          } catch (e) {
            // something went wrong either parsing the data,
            // or the data structure is incompatible with the current store schema
            // so we'll just ignore it and try to load the store from the server
            console.error(e);
          }

          // await new Promise((resolve) => setTimeout(resolve, 4000));

          if (!store) {
            const response = await hc.me.$get();
            if (response.ok) {
              const me = await response.json();
              store = GlobalStore.create(apiDataToStoreSnapshot(me));
              saveStore(store, session.user.id);
            } else {
              error = new Error("Failed to load store");
            }
          }
        }
        return { store, error };
      }
      if (session) {
        initStore()
          .then(({ store, error }) =>
            // @ts-expect-error - this is fine
            setStoreContext({ loading: false, store, error }),
          )
          .catch((error) => {
            setStoreContext({ loading: false, store: null, error });
          });
      }
    }
  }, [session, storeContext.store, hc]);

  return (
    <GlobalStoreContext.Provider
      value={{
        ...storeContext,
        refreshStore: refreshStoreCallback,
        clearStore: clearStoreCallback,
      }}
    >
      {children}
    </GlobalStoreContext.Provider>
  );
}

async function clearStore(store: GlobalStoreType, userId: string) {
  storageAdapter.removeItem(`@globalStore:${userId}`);
  applySnapshot(store, {});
  return store;
}

async function saveStore(store: GlobalStoreType, userId: string) {
  const data = JSON.stringify(getSnapshot(store));
  storageAdapter.setItem(`@globalStore:${userId}`, data);
  return store;
}

async function refreshStore(store: GlobalStoreType, hc: HonoClient) {
  const response = await hc.me.$get();
  if (response.ok) {
    const me = await response.json();
    applySnapshot(store, apiDataToStoreSnapshot(me));
    return store;
  }
  throw new Error("Failed to refresh store");
}

function apiDataToStoreSnapshot(
  data: InferResponseType<HonoClient["me"]["$get"]>,
) {
  const out: SnapshotIn<typeof GlobalStore> = {
    personalAccount: undefined,
    organizations: undefined,
  };
  const { personalAccount, organizations } = data;
  if (personalAccount) {
    out.personalAccount = {
      ...personalAccount,
      role: "OWNER",
    };
  }
  if (organizations) {
    out.organizations = organizations.reduce(
      (acc: Record<string, SnapshotIn<typeof Account>>, org) => {
        acc[org.id.toString()] = {
          ...org,
        };
        return acc;
      },
      {},
    );
  }
  return out;
}
